import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AiAgentService from '../../services/aiAgent.service';
import { cueLabelSx, getTokens, metaSx } from './aiAgentTheme';

const STATUS = {
    ringing: { label: 'zvonilo', tone: 'muted' },
    answered: { label: 'prijaté', tone: 'live' },
    completed: { label: 'dokončené', tone: 'ok' },
    missed: { label: 'nezdvihnuté', tone: 'muted' },
    failed: { label: 'nespojené', tone: 'muted' },
};

/** Telefonátu chýba meno, keď číslo nepatrí nikomu z firmy – vtedy je menom číslo. */
const callTitle = (row) =>
    row?.employee_name || row?.phone_number || (row?.employee_id ? `#${row.employee_id}` : 'Neznámy');

const formatWhen = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString('sk-SK', { dateStyle: 'short', timeStyle: 'short' });
};

/** Záznam hovorov – čítaný ako hovorový denník, nie ako tabuľka. */
const AiCallHistory = ({ reloadKey }) => {
    const theme = useTheme();
    const tokens = getTokens(theme.palette.mode === 'dark', theme.palette.primary.main);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [confirmClear, setConfirmClear] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () => {
        setLoading(true);
        AiAgentService.getHistory()
            .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    };

    useEffect(load, [reloadKey]);

    const removeOne = (row, event) => {
        event.stopPropagation();
        setBusy(true);
        AiAgentService.deleteHistoryItem(row.call_id)
            .then(() => setRows((prev) => prev.filter((r) => r.call_id !== row.call_id)))
            .finally(() => setBusy(false));
    };

    const removeAll = () => {
        setBusy(true);
        AiAgentService.deleteHistory()
            .then(() => {
                setRows([]);
                setConfirmClear(false);
            })
            .finally(() => setBusy(false));
    };

    const openDetail = (row) => {
        setDetailLoading(true);
        setDetail({ ...row, transcript: [] });
        AiAgentService.getHistoryDetail(row.call_id)
            .then((res) => setDetail(res.data))
            .catch(() => setDetail(null))
            .finally(() => setDetailLoading(false));
    };

    const dotColor = (tone) =>
        tone === 'live' ? tokens.accent : tone === 'ok' ? theme.palette.success.main : tokens.spine;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 600, color: tokens.ink }}>
                    Záznam hovorov
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        size="small"
                        onClick={load}
                        sx={{ ...cueLabelSx(tokens), minWidth: 0, p: 0.5, borderRadius: 0 }}
                    >
                        Obnoviť
                    </Button>
                    <Button
                        size="small"
                        onClick={() => setConfirmClear(true)}
                        disabled={rows.length === 0 || busy}
                        sx={{ ...cueLabelSx(tokens), minWidth: 0, p: 0.5, borderRadius: 0, color: tokens.rec }}
                    >
                        Vymazať všetko
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={20} />
                </Box>
            ) : rows.length === 0 ? (
                <Typography sx={{ color: tokens.inkMuted, fontSize: 14, py: 2 }}>
                    Zatiaľ žiadne hovory. Prvý spustíš formulárom vľavo.
                </Typography>
            ) : (
                <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
                    {rows.map((row) => {
                        const status = STATUS[row.status] || { label: row.status, tone: 'muted' };
                        return (
                            <Box
                                key={row.call_id}
                                onClick={() => openDetail(row)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    py: 1.25,
                                    borderBottom: `1px solid ${tokens.hairline}`,
                                    cursor: 'pointer',
                                    '&:hover .row-remove': { opacity: 1 },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        bgcolor: dotColor(status.tone),
                                        mt: '7px',
                                        flexShrink: 0,
                                    }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                        <Typography sx={{ fontSize: 14.5, color: tokens.ink }} noWrap>
                                            {callTitle(row)}
                                        </Typography>
                                        {row.channel === 'phone' ? (
                                            <Typography sx={cueLabelSx(tokens)}>telefón</Typography>
                                        ) : null}
                                        {row.recorded ? (
                                            <Typography sx={{ ...cueLabelSx(tokens), color: tokens.rec }}>
                                                prepis
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <Typography sx={metaSx(tokens)}>
                                        {formatWhen(row.started_at)} · {status.label}
                                        {row.duration_sec ? ` · ${row.duration_sec}s` : ''}
                                        {row.channel === 'phone' && row.employee_name && row.phone_number
                                            ? ` · ${row.phone_number}`
                                            : ''}
                                    </Typography>
                                    {row.error_text ? (
                                        <Typography sx={{ ...metaSx(tokens), color: tokens.rec, mt: 0.25 }} noWrap>
                                            {row.error_text}
                                        </Typography>
                                    ) : null}
                                    {row.task ? (
                                        <Typography
                                            sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.25 }}
                                            noWrap
                                        >
                                            {row.task}
                                        </Typography>
                                    ) : null}
                                </Box>
                                <IconButton
                                    className="row-remove"
                                    size="small"
                                    aria-label="Vymazať záznam"
                                    onClick={(event) => removeOne(row, event)}
                                    disabled={busy}
                                    sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity .15s ease' }}
                                >
                                    <CloseIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontSize: 20, fontWeight: 600 }}>
                    {detail ? callTitle(detail) : 'Hovor'}
                    <Typography sx={{ ...metaSx(tokens), mt: 0.5 }}>
                        {formatWhen(detail?.started_at)}
                        {detail?.duration_sec ? ` · ${detail.duration_sec}s` : ''}
                        {detail?.channel === 'phone' ? ' · telefonát' : ''}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {detail?.error_text ? (
                        <Box sx={{ mb: 2.5 }}>
                            <Typography sx={{ ...cueLabelSx(tokens), color: tokens.rec }}>Hovor sa nespojil</Typography>
                            <Typography sx={{ fontSize: 14, mt: 0.5 }}>{detail.error_text}</Typography>
                        </Box>
                    ) : null}

                    {detail?.task ? (
                        <Box sx={{ mb: 2.5 }}>
                            <Typography sx={cueLabelSx(tokens)}>Zadanie pre hovor</Typography>
                            <Typography sx={{ fontSize: 14, mt: 0.5 }}>{detail.task}</Typography>
                        </Box>
                    ) : null}

                    {detailLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : detail?.recorded ? (
                        (detail.transcript || []).length ? (
                            (detail.transcript || []).map((item, index) => {
                                const fromAgent = item.role === 'assistant';
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '92px 1fr',
                                            gap: 1.5,
                                            py: 0.75,
                                            borderTop: index ? `1px solid ${tokens.hairline}` : 'none',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                ...cueLabelSx(tokens),
                                                color: fromAgent ? tokens.accent : tokens.inkMuted,
                                                pt: '3px',
                                            }}
                                        >
                                            {fromAgent ? 'Asistent' : 'Človek'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 14.5, lineHeight: 1.55 }}>{item.text}</Typography>
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography sx={{ color: tokens.inkMuted, fontSize: 14 }}>
                                Prepis je prázdny — hovor skončil skôr, než niekto prehovoril.
                            </Typography>
                        )
                    ) : (
                        <Typography sx={{ color: tokens.inkMuted, fontSize: 14 }}>
                            Tento hovor sa nezaznamenával. Prepis zapneš pri spustení hovoru.
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
                <DialogTitle sx={{ fontWeight: 600 }}>Vymazať celý záznam?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14 }}>
                        Zmaže sa {rows.length} hovorov aj s prepismi. Späť sa to vrátiť nedá.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmClear(false)} disabled={busy} sx={{ borderRadius: 0 }}>
                        Zrušiť
                    </Button>
                    <Button
                        onClick={removeAll}
                        disabled={busy}
                        variant="contained"
                        disableElevation
                        sx={{ borderRadius: 0, bgcolor: tokens.rec, '&:hover': { bgcolor: tokens.rec } }}
                    >
                        Vymazať
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AiCallHistory;
